import { UploadSimple } from "@phosphor-icons/react";
import { forwardRef, useRef, useState, type DragEvent, type HTMLAttributes } from "react";
import { cn } from "@/lib/cn";

export type FileDropzoneProps = Omit<HTMLAttributes<HTMLDivElement>, "onDrop"> & {
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  error?: boolean;
  title?: string;
  description?: string;
  browseLabel?: string;
  onFilesChange?: (files: File[]) => void;
};

export const FileDropzone = forwardRef<HTMLDivElement, FileDropzoneProps>(
  ({ className, accept, multiple, disabled, error, title = "Drop files here", description = "Drag and drop files, or browse from your device.", browseLabel = "Browse files", onFilesChange, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [dragActive, setDragActive] = useState(false);

    const commitFiles = (files: FileList | File[]) => {
      if (disabled) return;
      onFilesChange?.(Array.from(files));
    };

    const handleDrag = (event: DragEvent<HTMLDivElement>, active: boolean) => {
      event.preventDefault();
      event.stopPropagation();
      if (!disabled) setDragActive(active);
    };

    return (
      <div
        ref={ref}
        className={cn(
          "bfrs-flex bfrs-min-h-36 bfrs-cursor-pointer bfrs-flex-col bfrs-items-center bfrs-justify-center bfrs-rounded-xl bfrs-border bfrs-border-dashed bfrs-bg-surface-card bfrs-px-5 bfrs-py-6 bfrs-text-center bfrs-outline-none bfrs-transition focus-within:bfrs-ring-2 focus-within:bfrs-ring-border-focus/20",
          dragActive ? "bfrs-border-primary bfrs-bg-primary-soft" : "bfrs-border-border-control",
          error && "bfrs-border-danger bfrs-bg-danger-soft/40",
          disabled && "bfrs-cursor-not-allowed bfrs-bg-surface-muted bfrs-opacity-70",
          className
        )}
        onClick={() => !disabled && inputRef.current?.click()}
        onDragEnter={(event) => handleDrag(event, true)}
        onDragOver={(event) => handleDrag(event, true)}
        onDragLeave={(event) => handleDrag(event, false)}
        onDrop={(event) => {
          handleDrag(event, false);
          commitFiles(event.dataTransfer.files);
        }}
        {...props}
      >
        <input ref={inputRef} type="file" accept={accept} multiple={multiple} disabled={disabled} className="bfrs-sr-only" onChange={(event) => event.currentTarget.files && commitFiles(event.currentTarget.files)} />
        <span className="bfrs-mb-3 bfrs-flex bfrs-size-10 bfrs-items-center bfrs-justify-center bfrs-rounded-pill bfrs-bg-primary-soft bfrs-text-primary">
          <UploadSimple size={20} weight="bold" />
        </span>
        <span className="bfrs-text-sm bfrs-font-semibold bfrs-text-text-primary">{title}</span>
        <span className="bfrs-mt-1 bfrs-max-w-sm bfrs-text-xs bfrs-leading-5 bfrs-text-text-secondary">{description}</span>
        <span className="bfrs-mt-3 bfrs-text-xs bfrs-font-semibold bfrs-text-primary">{browseLabel}</span>
      </div>
    );
  }
);

FileDropzone.displayName = "FileDropzone";
